import {
  Counter,
  Ticket,
  TicketAction,
  TicketStatus,
  useGetWaitingListTicketsQuery,
  useUpdateTicketStatusMutation,
} from "@/services/LiveQueueService";
import { TicketCard } from "./TicketCard";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/Button";
import { ScrollArea, ScrollBar } from "@/components/ui/ScrollArea";
import { useTicket } from "@/context/TicketContext";
import { useWebSocket } from "@/constants/websocket/Websocket";

export default function WaitingList({ eventId }: { eventId?: number }) {
  const { ws } = useWebSocket();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [ticketNumber, setTicketNumber] = useState("");
  const [debouncedTicketNumber, setDebouncedTicketNumber] = useState("");
  const { isCounterOpen } = useTicket();
  const [updateStatus] = useUpdateTicketStatusMutation();

  // Debounce logic
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedTicketNumber(ticketNumber); // Update debounced value after delay
    }, 300); // 300ms delay

    return () => clearTimeout(debounceTimeout); // Clear timeout on component unmount or value change
  }, [ticketNumber]);

  const today = new Date();
  const localDate = today.toLocaleDateString("en-CA"); // 'YYYY-MM-DD' format in local time
  const queryParams = new URLSearchParams({
    // dept_tag_id: userInfo?.department_id?.toString() ?? "0",
    counter_id: userInfo?.counter_id?.toString() ?? "0",
    date_filter: localDate,
    sort_by: "rerouted_at",
    sort_order: "asc",
    ...(debouncedTicketNumber && { search_key: debouncedTicketNumber }), // Use debounced value
    ...(eventId && { event_id: eventId.toString() }),
  });

  // Add multiple `status` parameters
  [TicketStatus.NEW, TicketStatus.HOLD, TicketStatus.CHECKED_IN].forEach(
    (status) => {
      queryParams.append("status", status);
    }
  );

  const {
    data: tickets,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetWaitingListTicketsQuery(
    { query: queryParams.toString() },
    { refetchOnMountOrArgChange: true }
  ); // Convert to string
  
  // Listen to websocket messages and refetch when the org matches, similar to DepartmentGrid
  useEffect(() => {
    if (ws) {
      ws.onmessage = (event) => {
        try {
          if (!event.data) return;
          let parsed;
          if (typeof event.data === "string") {
            try {
              parsed = JSON.parse(event.data);
            } catch {
              try {
                const fixedJson = event.data
                  .replace(/(\w+):/g, '"$1":')
                  .replace(/'/g, '"');
                parsed = JSON.parse(fixedJson);
              } catch {
                try {
                  const jsObjStr = event.data.trim();
                  // eslint-disable-next-line no-new-func
                  parsed = Function("return " + jsObjStr)();
                } catch {
                  return;
                }
              }
            }
          } else {
            return;
          }

          const messageOrgId = parsed?.org_id;
          const userOrgId = userInfo?.org_id;
          if (
            messageOrgId !== undefined &&
            userOrgId !== undefined &&
            String(messageOrgId) === String(userOrgId)
          ) {
            refetch();
          }
        } catch (err) {
          // swallow errors from malformed messages
          console.error("Error processing WebSocket message:", err);
        }
      };
    }

    return () => {
      if (ws) {
        ws.onmessage = null;
      }
    };
  }, [ws, userInfo, refetch]);
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTicketNumber(event.target.value.trim()); // Update the immediate value
  };
 
  const triggerLambda =async()=>{
    await updateStatus({
                  ticket_id: Number(0),
                  action: "COMPLETE" as TicketAction,
                  staff_id: String(userInfo?.id ?? 0),
                  trigger_lambda: false,
                }).unwrap()
  }

  if (isLoading) {
    // Loading skeleton
    return (
      <div className="p-6 mx-auto">
        <h1 className="text-xl font-semibold mb-6">Waiting List</h1>
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border border-gray-200 animate-pulse space-y-3"
            >
              <div className="h-6 bg-gray-300 rounded w-1/3"></div>
              <div className="flex flex-wrap gap-4 justify-between items-center">
                <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError || !tickets) {
    // Error state with retry button
    return (
      <div className="p-6 max-w-6xl mx-auto text-center">
        <h1 className="text-xl font-semibold mb-4 text-red-600">
          Error Fetching Tickets
        </h1>
        <p className="text-gray-600 mb-6">
          Something went wrong. Please try again.
        </p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-400 hover:text-black transition"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isCounterOpen) {
    return (
      <div className="p-6 max-w-6xl mx-auto text-center">
        <h1 className="text-xl font-semibold mb-4 text-gray-700">
          Counter is Closed
        </h1>
        <p className="text-gray-600">
          Please open the counter to view the waiting list.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-row justify-between ">
        <h1 className="text-xl font-semibold mb-6">Waiting List</h1>
        <Button
          onClick={() => {
            refetch();
            triggerLambda()
          }}
          variant={"outline"}
          className="w-fit mb-4 hover:bg-yellow-300 hover:text-black gap-2"
        >
          {" "}
          <i className="fa-solid fa-arrow-rotate-left"></i>
          Refresh Ticket List
        </Button>
      </div>
      <div className="mb-6 relative">
        <input
          type="text"
          placeholder="Search"
          value={ticketNumber}
          onChange={handleSearchChange}
          className="w-[60%] p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <i className="fa-solid fa-magnifying-glass text-gray-400"></i>
        </div>
      </div>
      <ScrollArea className="w-full custom-scrollbar  relative max-h-[30rem]">
        <ScrollBar orientation="vertical" className="space-x-2" />
        <div
          className={`${tickets.counters?.length ? "grid grid-cols-1 2xl:grid-cols-2 gap-2  border rounded-lg p-2 w-[99%]" : "border p-5 rounded-lg"} `}
        >
          {tickets && tickets.counters?.length > 0 &&
          tickets.counters.some((counter) => counter.tickets.length > 0) ? (
            tickets.counters.map((counter: Counter) =>
              counter.tickets.map((ticket: Ticket, index: number) => (
                <TicketCard
                  key={ticket.id}
                  r_ticket_id={Number(ticket.id)}
                  userId={Number(ticket.user_id)}
                  serviceId={Number(counter.service.id)}
                  ticketNumber={ticket.number}
                  customerName={`${ticket.user_details.first_name} ${ticket.user_details.last_name}`}
                  serviceName={counter.service.name}
                  waitTime={ticket.eta.toString()}
                  createdAt={ticket.created_at}
                  isHighlighted={ticket.status}
                  served_at={ticket.served_at}
                  title={ticket.title}
                  description={ticket.description}
                  ticketId={ticket.ticket_id}
                  ticketDate={ticket.date}
                  ticketTime={ticket.time}
                  path={ticket.path}
                  eventId={ticket.event_id}
                />
              ))
            )
          ) : (
            <p className="text-center text-gray-500 w-full">
              {ticketNumber
                ? "No Ticket matches the search criteria"
                : "Tickets are currently not available."}
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
